/**
 * BroadcastsModal.tsx — mass-message (broadcast) panel (owner request 2026-07-02).
 *
 * Relocated from the chatter app into @crm/chat (2026-07-20) so BOTH the chatter surface and
 * the admin full-parity chat surface use ONE implementation (no fork). Wired into ChatWorkspace
 * as an optional injected slot (BroadcastsModal prop); both hosts pass this same component.
 *
 * Opened from the "Mass messages" button at the top of the conversation list. Lets an operator
 * (chatter self-service, or admin full-parity — owner 2026-07-20):
 *   - see existing campaigns for the active creator's account(s)
 *   - create a broadcast (text, or vault media as PPV/free) to one or more Maloum fan lists
 *   - send it (approve), pause/resume, cancel, or delete it
 *
 * Everything is account-scoped server-side (a chatter can only touch an assigned account; admins
 * bypass). The message is authored in English and translated EN→DE server-side (fail-closed) and
 * CSAM-screened at create + approve (+ media screened per-send). The actual Maloum drip stays
 * behind SEND_LIVE_ARMED; free (unpriced) media additionally requires FREE_MEDIA_SEND_ARMED —
 * the server rejects a free-media campaign at create while disarmed.
 */

import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, Badge, useToast } from '@crm/ui';
import {
  broadcastsApi,
  type CampaignDto,
  type CreatorAccountSummary,
  ApiRequestError,
} from '../lib/api-client.js';
import { VaultPickerModal } from './VaultPickerModal.js';
import { formatMinorAmount } from '../lib/format.js';

interface BroadcastsModalProps {
  open: boolean;
  onClose: () => void;
  creatorName: string;
  /** The active creator's accounts — a broadcast targets exactly one. */
  accounts: CreatorAccountSummary[];
}

/** Map a campaign status to a Badge variant + label. */
function statusBadge(status: string): { variant: 'default' | 'success' | 'warning' | 'danger' | 'info' | 'outline'; label: string } {
  switch (status) {
    case 'draft':
      return { variant: 'outline', label: 'Draft' };
    case 'pending_approval':
      return { variant: 'info', label: 'Ready to send' };
    case 'scheduled':
      return { variant: 'info', label: 'Scheduled' };
    case 'active':
      return { variant: 'success', label: 'Sending' };
    case 'paused':
      return { variant: 'warning', label: 'Paused' };
    case 'completed':
      return { variant: 'default', label: 'Completed' };
    case 'cancelled':
      return { variant: 'danger', label: 'Cancelled' };
    default:
      return { variant: 'default', label: status };
  }
}

function errMsg(e: unknown, fallback: string): string {
  return e instanceof ApiRequestError ? e.message : fallback;
}

export function BroadcastsModal({ open, onClose, creatorName, accounts }: BroadcastsModalProps) {
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  const [accountId, setAccountId] = React.useState<string>(accounts[0]?.accountId ?? '');
  const [mode, setMode] = React.useState<'list' | 'create'>('list');

  // Keep the selected account valid as the active creator changes.
  React.useEffect(() => {
    if (!accounts.some((a) => a.accountId === accountId)) {
      setAccountId(accounts[0]?.accountId ?? '');
    }
  }, [accounts, accountId]);

  const campaignsQuery = useQuery({
    queryKey: ['broadcasts', accountId],
    enabled: open && !!accountId,
    queryFn: () => broadcastsApi.list(accountId),
    refetchInterval: open ? 8000 : false, // reflect drip progress while the panel is open
  });

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ['broadcasts', accountId] });

  const approveMut = useMutation({
    mutationFn: (id: string) => broadcastsApi.approve(id),
    onSuccess: () => {
      addToast('Broadcast sent', 'success');
      invalidate();
    },
    onError: (e) => addToast(errMsg(e, 'Failed to send broadcast'), 'danger'),
  });
  const pauseMut = useMutation({
    mutationFn: (id: string) => broadcastsApi.pause(id),
    onSuccess: () => { addToast('Paused', 'success'); invalidate(); },
    onError: (e) => addToast(errMsg(e, 'Failed to pause'), 'danger'),
  });
  const resumeMut = useMutation({
    mutationFn: (id: string) => broadcastsApi.resume(id),
    onSuccess: () => { addToast('Resumed', 'success'); invalidate(); },
    onError: (e) => addToast(errMsg(e, 'Failed to resume'), 'danger'),
  });
  const cancelMut = useMutation({
    mutationFn: (id: string) => broadcastsApi.cancel(id),
    onSuccess: () => { addToast('Cancelled', 'success'); invalidate(); },
    onError: (e) => addToast(errMsg(e, 'Failed to cancel'), 'danger'),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => broadcastsApi.delete(id),
    onSuccess: () => { addToast('Mass message deleted', 'success'); invalidate(); },
    onError: (e) => addToast(errMsg(e, 'Failed to delete'), 'danger'),
  });

  const campaigns = campaignsQuery.data ?? [];

  return (
    <Dialog open={open} onClose={onClose} title={`Mass messages — ${creatorName}`} className="max-w-2xl">
      <div className="flex flex-col gap-3">
        {/* Account selector (only when the creator has more than one account) */}
        {accounts.length > 1 && (
          <label className="flex items-center gap-2 text-sm text-text-muted">
            Account
            <select
              value={accountId}
              onChange={(e) => setAccountId(e.target.value)}
              className="bg-bg-input text-text border border-border rounded-md px-2 py-1 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
            >
              {accounts.map((a, i) => (
                <option key={a.accountId} value={a.accountId}>
                  Account {i + 1}
                </option>
              ))}
            </select>
          </label>
        )}

        {mode === 'create' ? (
          <CreateBroadcastForm
            accountId={accountId}
            onCancel={() => setMode('list')}
            onCreated={() => {
              setMode('list');
              invalidate();
            }}
          />
        ) : (
          <>
            <div className="flex items-center justify-between">
              <span className="text-sm text-text-muted">
                {campaignsQuery.isLoading
                  ? 'Loading…'
                  : `${campaigns.length} campaign${campaigns.length === 1 ? '' : 's'}`}
              </span>
              <button
                type="button"
                onClick={() => setMode('create')}
                disabled={!accountId}
                className="rounded-md bg-accent px-3 py-1.5 text-sm font-semibold text-accent-fg hover:bg-accent/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 disabled:opacity-50"
              >
                + New broadcast
              </button>
            </div>

            {campaignsQuery.isError ? (
              <p className="text-danger text-sm py-6 text-center">Failed to load broadcasts.</p>
            ) : campaigns.length === 0 && !campaignsQuery.isLoading ? (
              <p className="text-text-muted text-sm py-6 text-center">
                No mass messages yet. Create one to reach a fan list.
              </p>
            ) : (
              <ul className="flex flex-col gap-2 max-h-[50vh] overflow-y-auto">
                {campaigns.map((c) => (
                  <CampaignRow
                    key={c.id}
                    campaign={c}
                    onSend={() => approveMut.mutate(c.id)}
                    onPause={() => pauseMut.mutate(c.id)}
                    onResume={() => resumeMut.mutate(c.id)}
                    onCancel={() => cancelMut.mutate(c.id)}
                    onDelete={() => deleteMut.mutate(c.id)}
                    busy={
                      approveMut.isPending ||
                      pauseMut.isPending ||
                      resumeMut.isPending ||
                      cancelMut.isPending ||
                      deleteMut.isPending
                    }
                  />
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Campaign row
// ---------------------------------------------------------------------------

function CampaignRow({
  campaign,
  onSend,
  onPause,
  onResume,
  onCancel,
  onDelete,
  busy,
}: {
  campaign: CampaignDto;
  onSend: () => void;
  onPause: () => void;
  onResume: () => void;
  onCancel: () => void;
  onDelete: () => void;
  busy: boolean;
}) {
  const badge = statusBadge(campaign.status);
  const canSend = campaign.status === 'pending_approval' || campaign.status === 'draft';
  const canPause = campaign.status === 'active';
  const canResume = campaign.status === 'paused';
  const canCancel = ['active', 'scheduled', 'paused'].includes(campaign.status);
  // Two-step inline confirm for Delete — auto-resets after a few seconds.
  const [confirmingDelete, setConfirmingDelete] = React.useState(false);
  React.useEffect(() => {
    if (!confirmingDelete) return;
    const t = setTimeout(() => setConfirmingDelete(false), 4000);
    return () => clearTimeout(t);
  }, [confirmingDelete]);

  const hasMedia = campaign.messageKind !== 'text';

  return (
    <li className="rounded-md border border-border bg-bg-card p-3 flex flex-col gap-2">
      <div className="flex items-start justify-between gap-2">
        <p className="text-sm text-text min-w-0 line-clamp-2">{campaign.messageEn}</p>
        <div className="flex items-center gap-1.5 shrink-0">
          {campaign.isPpv && (
            <Badge variant="success">
              PPV{campaign.ppvPriceMinor != null
                ? ` ${formatMinorAmount(campaign.ppvPriceMinor, campaign.ppvPriceCurrency ?? 'EUR')}`
                : ''}
            </Badge>
          )}
          {hasMedia && !campaign.isPpv && <Badge variant="info">Media</Badge>}
          <Badge variant={badge.variant}>{badge.label}</Badge>
        </div>
      </div>
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs text-text-subtle">
          Got it: {campaign.sentCount} / {campaign.totalRecipients}
          {campaign.failedCount > 0 ? ` · ${campaign.failedCount} failed` : ''}
        </span>
        <div className="flex items-center gap-1.5">
          {canSend && (
            <button
              type="button"
              onClick={onSend}
              disabled={busy}
              className="rounded bg-accent px-2 py-1 text-xs font-semibold text-accent-fg hover:bg-accent/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 disabled:opacity-50"
            >
              Send now{campaign.totalRecipients > 0 ? ` (${campaign.totalRecipients})` : ''}
            </button>
          )}
          {canPause && (
            <button
              type="button"
              onClick={onPause}
              disabled={busy}
              className="rounded border border-border px-2 py-1 text-xs text-text-muted hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 disabled:opacity-50"
            >
              Pause
            </button>
          )}
          {canResume && (
            <button
              type="button"
              onClick={onResume}
              disabled={busy}
              className="rounded border border-border px-2 py-1 text-xs text-text-muted hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 disabled:opacity-50"
            >
              Resume
            </button>
          )}
          {canCancel && (
            <button
              type="button"
              onClick={onCancel}
              disabled={busy}
              className="rounded border border-border px-2 py-1 text-xs text-danger hover:bg-danger/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-danger/60 disabled:opacity-50"
            >
              Cancel
            </button>
          )}
          <button
            type="button"
            onClick={() => {
              if (confirmingDelete) {
                setConfirmingDelete(false);
                onDelete();
              } else {
                setConfirmingDelete(true);
              }
            }}
            disabled={busy}
            className={[
              'rounded px-2 py-1 text-xs focus:outline-none focus-visible:ring-2 focus-visible:ring-danger/60 disabled:opacity-50',
              confirmingDelete
                ? 'bg-danger text-accent-fg font-semibold'
                : 'border border-border text-danger hover:bg-danger/10',
            ].join(' ')}
            title={
              ['active', 'completed'].includes(campaign.status)
                ? 'Removes it from this list. Messages already delivered to fans stay in their chats.'
                : 'Delete this mass message'
            }
          >
            {confirmingDelete ? 'Really delete?' : 'Delete'}
          </button>
        </div>
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// Create form
// ---------------------------------------------------------------------------

function CreateBroadcastForm({
  accountId,
  onCancel,
  onCreated,
}: {
  accountId: string;
  onCancel: () => void;
  onCreated: () => void;
}) {
  const { addToast } = useToast();
  const [messageEn, setMessageEn] = React.useState('');
  const [selectedLists, setSelectedLists] = React.useState<string[]>([]);
  // Vault media attachment (owner batch 2026-07-02) — same shape SendComposer uses.
  const [vaultOpen, setVaultOpen] = React.useState(false);
  const [media, setMedia] = React.useState<{
    uploadId: string;
    type: string;
    width: number;
    height: number;
  } | null>(null);
  // PPV price in whole EUR (Maloum settles PPV in EUR only). Empty = free media.
  const [priceEur, setPriceEur] = React.useState('');

  const listsQuery = useQuery({
    queryKey: ['broadcast-lists', accountId],
    enabled: !!accountId,
    queryFn: () => broadcastsApi.getChatLists(accountId),
  });

  const priceMinor = priceEur.trim() === '' ? null : Math.round(parseFloat(priceEur) * 100);
  const priceInvalid =
    priceEur.trim() !== '' && (!Number.isFinite(priceMinor) || (priceMinor as number) <= 0);
  const isPpv = media !== null && priceMinor !== null && !priceInvalid;

  const createMut = useMutation({
    mutationFn: () =>
      broadcastsApi.create({
        messageEn: messageEn.trim(),
        accountId,
        includeLists: selectedLists,
        messageKind: media ? (isPpv ? 'chat_product' : 'free_media') : 'text',
        ...(media
          ? {
              vaultMediaId: media.uploadId,
              vaultMediaMeta: {
                type: media.type === 'video' ? ('video' as const) : ('picture' as const),
                width: media.width,
                height: media.height,
              },
            }
          : {}),
        ...(isPpv
          ? { isPpv: true, ppvPriceMinor: priceMinor as number, ppvPriceCurrency: 'EUR' }
          : {}),
      }),
    onSuccess: () => {
      addToast('Broadcast created — review and send it below', 'success');
      onCreated();
    },
    onError: (e) => addToast(errMsg(e, 'Failed to create broadcast'), 'danger'),
  });

  const lists = listsQuery.data ?? [];
  const canSubmit =
    messageEn.trim().length > 0 && selectedLists.length > 0 && !priceInvalid && !createMut.isPending;

  const toggleList = (id: string) =>
    setSelectedLists((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSubmit) createMut.mutate();
      }}
    >
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-text-muted">Message (English — sent to fans translated to German)</span>
        <textarea
          value={messageEn}
          onChange={(e) => setMessageEn(e.target.value)}
          rows={4}
          placeholder="Write your message in English…"
          className="bg-bg-input text-text border border-border rounded-md px-2 py-1.5 text-sm resize-y focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
        />
      </label>

      {/* Media + PPV price (owner batch 2026-07-02) */}
      <div className="flex flex-col gap-1.5 text-sm">
        <span className="text-text-muted">Media (optional)</span>
        {media ? (
          <div className="flex items-center gap-2 rounded-md border border-border bg-bg px-2 py-1.5">
            <Badge variant="info">{media.type === 'video' ? 'Video' : 'Photo'}</Badge>
            <span className="text-xs text-text-subtle">
              {media.width}×{media.height}
            </span>
            <button
              type="button"
              onClick={() => setMedia(null)}
              aria-label="Remove attached media"
              className="ml-auto rounded px-1.5 py-0.5 text-xs text-danger hover:bg-danger/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-danger/60"
            >
              Remove
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setVaultOpen(true)}
            className="self-start rounded-md border border-border px-3 py-1.5 text-sm text-text-muted hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
          >
            Attach media from vault
          </button>
        )}
        {media && (
          <label className="flex items-center gap-2 text-sm">
            <span className="text-text-muted shrink-0">PPV price €</span>
            <input
              type="number"
              min="0.01"
              step="0.01"
              inputMode="decimal"
              value={priceEur}
              onChange={(e) => setPriceEur(e.target.value)}
              placeholder="e.g. 15"
              className="w-28 bg-bg-input text-text border border-border rounded-md px-2 py-1.5 text-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
            />
            <span className="text-xs text-text-subtle">
              {isPpv
                ? `Fans pay ${formatMinorAmount(priceMinor as number, 'EUR')} to unlock.`
                : 'Leave empty to send as free media (if enabled on the server).'}
            </span>
          </label>
        )}
        {priceInvalid && (
          <span className="text-xs text-danger" role="alert">
            Price must be a positive amount (or leave it empty).
          </span>
        )}
      </div>

      <div className="flex flex-col gap-1 text-sm">
        <span className="text-text-muted">Audience (fan lists)</span>
        {listsQuery.isLoading ? (
          <span className="text-text-subtle text-xs">Loading lists…</span>
        ) : listsQuery.isError ? (
          <span className="text-danger text-xs" role="alert">
            Couldn’t load fan lists — {errMsg(listsQuery.error, 'server error')}.{' '}
            <button
              type="button"
              onClick={() => listsQuery.refetch()}
              className="underline hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
            >
              Retry
            </button>
          </span>
        ) : lists.length === 0 ? (
          <span className="text-text-subtle text-xs">
            No fan lists available for this account.
          </span>
        ) : (
          <div className="flex flex-col gap-1 max-h-40 overflow-y-auto border border-border rounded-md p-2">
            {lists.map((l) => (
              <label key={l.id} className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={selectedLists.includes(l.id)}
                  onChange={() => toggleList(l.id)}
                  className="accent-accent"
                />
                <span className="truncate">{l.friendlyName || l.name}</span>
                <span className="text-text-subtle text-xs ml-auto font-mono">{l.totalMemberCount}</span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-border px-3 py-1.5 text-sm text-text-muted hover:text-text focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!canSubmit}
          className="rounded-md bg-accent px-3 py-1.5 text-sm font-semibold text-accent-fg hover:bg-accent/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 disabled:opacity-50"
        >
          {createMut.isPending
            ? 'Creating…'
            : isPpv
              ? `Create PPV broadcast (${formatMinorAmount(priceMinor as number, 'EUR')})`
              : 'Create broadcast'}
        </button>
      </div>

      {/* Vault picker — portals above this dialog; no fanMemberId (not a per-fan send).
          Broadcasts stay single-media (the campaign shape carries one vaultMediaId), so the
          shared multi-select picker is constrained to one item here. */}
      <VaultPickerModal
        open={vaultOpen}
        onClose={() => setVaultOpen(false)}
        accountId={accountId}
        maxSelectable={1}
        onAttach={(picked) => {
          if (picked[0]) setMedia(picked[0]);
          setVaultOpen(false);
        }}
      />
    </form>
  );
}
